import { Box, MenuItem, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import {
  CustomButton,
  CustomSelect,
  CustomTextField,
  HorizontalForm,
  VerticalForm,
} from '../../../common/components';
import {
  GERENCIALES_DATE_RANGE_REPORTS,
  GERENCIALES_DEBT_STATUS_VIEW_OPTIONS,
  GERENCIALES_APP_SUBSCRIBERS_REPORT_TYPE,
  GERENCIALES_APP_USAGE_OPTIONS,
  GERENCIALES_ARCHIVED_CONTRACTS_REPORT_TYPE,
  GERENCIALES_PAYMENTS_REPORT_TYPE,
} from '../constants/gerencialesConstants';

/**
 * Filtros para el modulo de estadisticas gerenciales.
 * @param {Object} props - Props del componente.
 * @param {string} props.reportType - Tipo de reporte seleccionado.
 * @param {Array<{label: string, value: string}>} props.reportOptions - Opciones del selector.
 * @param {boolean} props.loading - Estado de carga.
 * @param {string|null} props.error - Mensaje de error.
 * @param {Object|null} props.header - Header de respuesta.
 * @param {string} props.dateStart - Fecha desde.
 * @param {string} props.dateEnd - Fecha hasta.
 * @param {Function} props.onDateChange - Handler de cambio de fechas.
 * @param {Function} props.onReportTypeChange - Handler de cambio de reporte.
 * @param {Function} props.onReload - Handler de recarga.
 * @param {Function} props.onDownloadPdf - Handler de descarga PDF.
 * @param {boolean} props.pdfCooldown - Bloquea descarga mientras se renderiza el grafico.
 * @param {boolean} props.disableDownload - Deshabilita descarga cuando no hay data.
 * @param {Function} props.onDownloadXlsx - Handler de descarga XLSX.
 * @param {boolean} props.disableDownloadXlsx - Deshabilita descarga XLSX.
 * @param {boolean} props.showDownloadXlsx - Muestra botón XLSX.
 * @param {Object} props.appSubscribersFilters - Filtros del reporte de app.
 * @param {Function} props.onAppSubscribersFilterChange - Handler de filtros de app.
 * @param {Object} props.archivedContractsFilters - Filtros del reporte de bajas.
 * @param {Function} props.onArchivedContractsFilterChange - Handler de filtros de bajas.
 * @param {string} props.reportView - Vista seleccionada en reportes con tabs.
 * @param {Function} props.onReportViewChange - Handler de cambio de vista.
 * @param {Array<Object>} props.releaseReasons - Motivos de baja disponibles.
 * @param {boolean} props.releaseReasonsLoading - Estado de carga de motivos de baja.
 * @param {string} props.paymentMonth - Filtro de mes para reporte de pagos.
 * @param {string} props.paymentYear - Filtro de año para reporte de pagos.
 * @param {string} props.paymentLegalEntity - Entidad legal recaudadora seleccionada.
 * @param {Function} props.onPaymentsFilterChange - Handler de cambio de filtros de pagos.
 * @param {Array<Object>} props.recLegalEntities - Entidades legales recaudadoras disponibles.
 * @param {boolean} props.recLegalEntitiesLoading - Estado de carga de entidades legales recaudadoras.
 * @returns {JSX.Element} Barra de filtros.
 */
const GerencialesFilters = ({
  reportType,
  reportOptions,
  loading,
  error,
  header,
  dateStart,
  dateEnd,
  onDateChange,
  onReportTypeChange,
  onReload,
  onDownloadPdf,
  pdfCooldown,
  disableDownload,
  onDownloadXlsx,
  disableDownloadXlsx,
  showDownloadXlsx,
  appSubscribersFilters,
  onAppSubscribersFilterChange,
  archivedContractsFilters,
  onArchivedContractsFilterChange,
  reportView,
  onReportViewChange,
  releaseReasons,
  releaseReasonsLoading,
  paymentMonth,
  paymentYear,
  paymentLegalEntity,
  onPaymentsFilterChange,
  recLegalEntities,
  recLegalEntitiesLoading,
}) => {
  const showDateFilters =
    GERENCIALES_DATE_RANGE_REPORTS.includes(reportType) ||
    reportType === GERENCIALES_ARCHIVED_CONTRACTS_REPORT_TYPE;
  const showAppSubscribersFilters = reportType === GERENCIALES_APP_SUBSCRIBERS_REPORT_TYPE;
  const showArchivedContractsFilters = reportType === GERENCIALES_ARCHIVED_CONTRACTS_REPORT_TYPE;
  const showReportView = showAppSubscribersFilters || showArchivedContractsFilters;
  const showPaymentsFilters = reportType === GERENCIALES_PAYMENTS_REPORT_TYPE;

  const monthOptions = [
    { label: 'Enero', value: '1' },
    { label: 'Febrero', value: '2' },
    { label: 'Marzo', value: '3' },
    { label: 'Abril', value: '4' },
    { label: 'Mayo', value: '5' },
    { label: 'Junio', value: '6' },
    { label: 'Julio', value: '7' },
    { label: 'Agosto', value: '8' },
    { label: 'Septiembre', value: '9' },
    { label: 'Octubre', value: '10' },
    { label: 'Noviembre', value: '11' },
    { label: 'Diciembre', value: '12' },
  ];
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 6 }, (_, index) => `${currentYear - index}`);

  return (
    <HorizontalForm
      sx={{
        flexWrap: 'wrap',
        rowGap: 2,
        columnGap: 2,
        overflowX: 'unset',
        alignItems: 'center',
      }}
    >
      <CustomSelect
        label="Tipo de reporte"
        size="medium"
        value={reportType}
        onChange={onReportTypeChange}
        sx={{ maxWidth: 280 }}
      >
        {reportOptions.map((option) => (
          <MenuItem key={option.value} value={option.value} sx={{ fontSize: 14 }}>
            {option.label}
          </MenuItem>
        ))}
      </CustomSelect>
      {showReportView && (
        <>
          <CustomSelect
            label="Vista"
            size="medium"
            value={reportView}
            onChange={onReportViewChange}
            sx={{ maxWidth: 120, mr: 0.5 }}
          >
            {GERENCIALES_DEBT_STATUS_VIEW_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </CustomSelect>
          {showAppSubscribersFilters && (
            <CustomSelect
              label="Uso app"
              name="appUsage"
              size="medium"
              value={appSubscribersFilters.appUsage}
              onChange={onAppSubscribersFilterChange}
              sx={{ maxWidth: 160, mr: 0.5 }}
            >
              {GERENCIALES_APP_USAGE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </CustomSelect>
          )}
          {showArchivedContractsFilters && (
            <>
              <CustomTextField
                label="Num. Contrato"
                name="contractNumber"
                value={archivedContractsFilters.contractNumber}
                onChange={onArchivedContractsFilterChange}
                sx={{ maxWidth: 160, mr: 0.5 }}
              />
              <CustomSelect
                label="Motivo baja"
                name="releaseReason"
                size="medium"
                value={archivedContractsFilters.releaseReason}
                onChange={onArchivedContractsFilterChange}
                sx={{ maxWidth: 220, mr: 0.5 }}
                disabled={releaseReasonsLoading}
              >
                <MenuItem value="">Todos</MenuItem>
                {releaseReasons.map((reason) => (
                  <MenuItem key={reason.uuid} value={reason.uuid}>
                    {reason.description}
                  </MenuItem>
                ))}
              </CustomSelect>
            </>
          )}
        </>
      )}
      {showDateFilters && (
        <>
          <CustomTextField
            label="Fecha Desde"
            type="date"
            name="dateStart"
            value={dateStart}
            onChange={onDateChange}
            InputLabelProps={{ shrink: true }}
            required
            sx={{ maxWidth: 180, mr: 0.5 }}
          />
          <CustomTextField
            label="Fecha Hasta"
            type="date"
            name="dateEnd"
            value={dateEnd}
            onChange={onDateChange}
            InputLabelProps={{ shrink: true }}
            required
            sx={{ maxWidth: 180, mr: 0.5 }}
          />
        </>
      )}
      {showPaymentsFilters && (
        <>
          <CustomSelect
            label="Mes"
            name="month"
            size="medium"
            value={paymentMonth}
            onChange={onPaymentsFilterChange}
            sx={{ maxWidth: 160, mr: 0.5 }}
          >
            <MenuItem value="">Todos</MenuItem>
            {monthOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </CustomSelect>
          <CustomSelect
            label="Año"
            name="year"
            size="medium"
            value={paymentYear}
            onChange={onPaymentsFilterChange}
            sx={{ maxWidth: 140, mr: 0.5 }}
          >
            <MenuItem value="">Todos</MenuItem>
            {yearOptions.map((yearValue) => (
              <MenuItem key={yearValue} value={yearValue}>
                {yearValue}
              </MenuItem>
            ))}
          </CustomSelect>
          <CustomSelect
            label="Entidad Legal Recaudadora"
            name="legalEntity"
            size="medium"
            value={paymentLegalEntity}
            onChange={onPaymentsFilterChange}
            sx={{ maxWidth: 280, mr: 0.5 }}
            disabled={recLegalEntitiesLoading}
          >
            <MenuItem value="">Todas</MenuItem>
            {recLegalEntities.map((entity) => (
              <MenuItem key={entity.uuid} value={entity.uuid}>
                {entity.legal_business_name || entity.company || entity.cuit}
              </MenuItem>
            ))}
          </CustomSelect>
        </>
      )}
      <CustomButton
        variant="warning"
        onClick={onReload}
        disabled={loading}
        buttonIcon={RefreshIcon}
        sx={{ mt: 1.5, mr: 0.5, pl: 4 }}
      />
      <VerticalForm withBorder={false} sx={{ mt: -3.5 }}>
        {error && (
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        )}
      </VerticalForm>
      <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'block' } }} />
      <CustomButton
        variant="base"
        onClick={onDownloadPdf}
        disabled={loading || disableDownload}
        isLoading={pdfCooldown}
        buttonIcon={PictureAsPdfIcon}
        sx={{ mt: 1.5 }}
      >
        PDF
      </CustomButton>
      {showDownloadXlsx && (
        <CustomButton
          variant="success"
          onClick={onDownloadXlsx}
          disabled={loading || disableDownloadXlsx}
          buttonIcon={TableChartIcon}
          sx={{ mt: 1.5, ml: 1.5 }}
        >
          XLSX
        </CustomButton>
      )}
    </HorizontalForm>
  );
};

export default GerencialesFilters;
